import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApi } from "src/api";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { PrtgInstanceStatus, PrtgSensor } from "src/questionnaires";
import { OpsanioApplianceData } from ".";
import {
  alertingSensors,
  deriveFleetHealth,
  deriveHealth,
  DerivedHealth,
  EMPTY_HEALTH,
  FleetHealth,
} from "./health";

/**
 * Per-appliance "engagement" counts from the surrounding tooling. Each is an
 * honest current count (0 when the integration is unconfigured/unreachable -
 * never fabricated).
 */
export interface EngagementCounts {
  /** Open GitLab issues for the appliance's project. */
  gitlabOpen: number;
  /** Open OTOBO tickets in the appliance's queue. */
  otoboOpen: number;
  /** Sentry user-feedback items scoped to the appliance. */
  feedback: number;
}

export const EMPTY_ENGAGEMENT: EngagementCounts = {
  gitlabOpen: 0,
  otoboOpen: 0,
  feedback: 0,
};

export interface FleetHealthEntry {
  appliance: OpsanioApplianceData;
  health: DerivedHealth;
  engagement: EngagementCounts;
  /** Sensors currently down / warning / unusual, worst first. */
  alerts: PrtgSensor[];
  /**
   * The PRTG request for this appliance failed (unreachable, not configured,
   * error). Its health is then the unmonitored default - never "healthy".
   */
  prtgFailed: boolean;
  /** HTTP status of a failed PRTG request, when one was returned. */
  prtgStatus: number | null;
}

export interface UseFleetHealthOptions {
  /**
   * Also fetch the GitLab / OTOBO / Sentry counts. Defaults to true; a view that
   * only shows sensor health (the wall) turns it off to save three calls per
   * appliance and sweep.
   */
  engagement?: boolean;
  /**
   * When every PRTG request of a sweep fails, keep the previous snapshot and
   * report an `outage` instead of replacing a known picture with "all
   * unmonitored". Only the auto-refreshing wall wants this.
   */
  keepOnOutage?: boolean;
}

export interface FleetHealthOutage {
  /** HTTP status of the failed PRTG requests, when one was returned. */
  status: number | null;
}

export interface UseFleetHealthResult {
  /** Per-appliance health, keyed by appliance id. */
  byId: Record<string, FleetHealthEntry>;
  fleet: FleetHealth;
  loading: boolean;
  /** {done,total} progress of the in-flight fan-out. */
  progress: { done: number; total: number };
  /** Whether a fan-out has completed at least once this session. */
  loaded: boolean;
  /** Epoch ms when the last sweep finished (successful or not). */
  lastRunAt: number | null;
  /** Epoch ms when the data currently shown was fetched. */
  lastSuccessAt: number | null;
  /** Set when the last sweep failed for every appliance (keepOnOutage only). */
  outage: FleetHealthOutage | null;
  run: () => void;
}

/**
 * Fleet-wide live health, fanned out across appliances.
 *
 * There is no fleet aggregate endpoint, so this issues one
 * `/prtg/instance/:name` call per appliance with a bounded concurrency pool
 * (so PRTG isn't hammered) and aggregates the honest per-appliance results.
 * Per-appliance failures degrade to an `errored` unmonitored entry - they never
 * abort the sweep and are never counted as healthy. Appliances without a VPC
 * name are skipped (unmonitored).
 *
 * The sweep is gated behind `run()` (call it from a CTA or auto-run once) so a
 * page visit doesn't silently trigger N network calls without intent.
 */
export const useFleetHealth = (
  appliances: OpsanioApplianceData[],
  concurrency: number = 4,
  options: UseFleetHealthOptions = {},
): UseFleetHealthResult => {
  const withEngagement = options.engagement ?? true;
  const keepOnOutage = options.keepOnOutage ?? false;
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();

  const [byId, setById] = useState<Record<string, FleetHealthEntry>>({});
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [lastRunAt, setLastRunAt] = useState<number | null>(null);
  const [lastSuccessAt, setLastSuccessAt] = useState<number | null>(null);
  const [outage, setOutage] = useState<FleetHealthOutage | null>(null);
  const runningRef = useRef(false);
  // The sweep reads the previous snapshot to decide whether to keep it.
  const byIdRef = useRef<Record<string, FleetHealthEntry>>({});

  const run = useCallback(async () => {
    if (runningRef.current) return;
    if (typeof keycloak?.token !== "string") return;
    const token = keycloak.token;
    runningRef.current = true;
    setLoading(true);

    const targets = appliances.filter((a) => !a.archived);
    setProgress({ done: 0, total: targets.length });
    const next: Record<string, FleetHealthEntry> = {};
    let cursor = 0;
    let done = 0;
    let attempted = 0;
    let failed = 0;
    let failedStatus: number | null = null;

    // Each fetcher is self-contained and never throws: failure / not-configured
    // (503) / unreachable all degrade to the honest "nothing reported" default.
    type HealthResult = {
      health: DerivedHealth;
      alerts: PrtgSensor[];
      failed: boolean;
      status: number | null;
    };
    const fetchHealth = async (name: string): Promise<HealthResult> => {
      const none = { health: EMPTY_HEALTH, alerts: [], status: null };
      if (name === "") return { ...none, failed: false };
      try {
        const { success, data, error, status } = await Get<{
          status: number;
          data: PrtgInstanceStatus;
        }>({
          path: `v2/${AnketaResourcesEnum.opsanio}/prtg/instance/${encodeURIComponent(
            name,
          )}`,
          responseType: "json",
          token,
        });
        if (!success || error) {
          return { ...none, failed: true, status: status || null };
        }
        const snapshot = data?.data ?? null;
        return {
          health: deriveHealth(snapshot),
          alerts: alertingSensors(
            (snapshot?.devices ?? []).flatMap((d) => d.sensors ?? []),
          ),
          failed: false,
          status: null,
        };
      } catch {
        return { ...none, failed: true };
      }
    };

    const fetchGitlabOpen = async (
      meta: OpsanioApplianceData["common"]["metadata"] | undefined,
    ): Promise<number> => {
      const project = (meta as any)?.project;
      const owner = (meta as any)?.owner;
      const stage = (meta as any)?.stage;
      if (!project || !owner || !stage) return 0;
      try {
        // Counts only - the full issue list (every issue, every page) is what
        // the flyout's GitLab tab loads when someone actually opens it.
        const { success, data, error } = await Get<any>({
          path: `v2/${AnketaResourcesEnum.opsanio}/gitlab/issues/${project}/${owner}/${stage}/count`,
          responseType: "json",
          token,
        });
        if (!success || error) return 0;
        const opened = (data as any)?.data?.opened;
        return typeof opened === "number" ? opened : 0;
      } catch {
        return 0;
      }
    };

    const fetchOtoboOpen = async (name: string): Promise<number> => {
      if (name === "") return 0;
      try {
        // One search per appliance (open states only) instead of loading the
        // whole ticket list just to count it.
        const { success, data, error } = await Get<any>({
          path: `v2/${AnketaResourcesEnum.opsanio}/otobo/instance/${encodeURIComponent(
            name,
          )}/count`,
          responseType: "json",
          token,
        });
        if (!success || error) return 0;
        const open = Number((data as any)?.data?.open);
        return Number.isFinite(open) ? open : 0;
      } catch {
        return 0;
      }
    };

    const fetchFeedback = async (name: string): Promise<number> => {
      if (name === "") return 0;
      try {
        const { success, data, error } = await Get<any>({
          path: `v2/${AnketaResourcesEnum.opsanio}/sentry/feedback/instance/${encodeURIComponent(
            name,
          )}`,
          responseType: "json",
          token,
        });
        if (!success || error) return 0;
        const fb = (data as any)?.data?.feedback;
        return Array.isArray(fb) ? fb.length : 0;
      } catch {
        return 0;
      }
    };

    const worker = async () => {
      while (cursor < targets.length) {
        const appliance = targets[cursor++];
        const meta = appliance.common?.metadata;
        const name = meta?.name?.trim() ?? "";
        // The four integrations are independent - fetch them concurrently so an
        // appliance waits on its slowest call, not the sum of all four.
        const [result, gitlabOpen, otoboOpen, feedback] = await Promise.all([
          fetchHealth(name),
          withEngagement ? fetchGitlabOpen(meta) : 0,
          withEngagement ? fetchOtoboOpen(name) : 0,
          withEngagement ? fetchFeedback(name) : 0,
        ]);
        if (name !== "") attempted += 1;
        if (result.failed) {
          failed += 1;
          failedStatus = failedStatus ?? result.status;
        }
        next[appliance.id] = {
          appliance,
          health: result.health,
          engagement: { gitlabOpen, otoboOpen, feedback },
          alerts: result.alerts,
          prtgFailed: result.failed,
          prtgStatus: result.status,
        };
        done += 1;
        setProgress({ done, total: targets.length });
      }
    };

    const pool = Array.from(
      { length: Math.min(concurrency, Math.max(1, targets.length)) },
      () => worker(),
    );
    await Promise.all(pool);

    const finishedAt = Date.now();
    const total = attempted > 0 && failed === attempted;
    if (
      keepOnOutage &&
      total &&
      Object.keys(byIdRef.current).length > 0
    ) {
      // PRTG is down for everyone: keep the last known picture, flag it stale.
      setOutage({ status: failedStatus });
    } else {
      byIdRef.current = next;
      setById(next);
      setOutage(keepOnOutage && total ? { status: failedStatus } : null);
      if (!total) setLastSuccessAt(finishedAt);
    }
    setLastRunAt(finishedAt);
    setLoading(false);
    setLoaded(true);
    runningRef.current = false;
  }, [appliances, concurrency, keycloak, Get, withEngagement, keepOnOutage]);

  // Auto-run once when appliances first become available.
  useEffect(() => {
    if (!loaded && !runningRef.current && appliances.length > 0) {
      run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliances.length]);

  const fleet = useMemo<FleetHealth>(
    () => deriveFleetHealth(Object.values(byId).map((e) => e.health)),
    [byId],
  );

  return {
    byId,
    fleet,
    loading,
    progress,
    loaded,
    lastRunAt,
    lastSuccessAt,
    outage,
    run,
  };
};
